import { TableCell, TableFooter, TableRow } from "@/components/ui/table";
import { useTranslation } from "react-i18next";
import { UseQueryResult } from "@tanstack/react-query";
import { SuccessFetchListResponse } from "@/services/api";
import { ProjectLimitSummary } from "@/types/projectLimit";
import { useMemo } from "react";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import { ProjectLimitSummaryHoursColumnDef } from "../../types/limits";
import { cn } from "@/lib/utils";
import useTableColumnVisibility from "@/components/data-table/hooks/useTableColumnVisibility";
import { MonthRange } from "./types";
import { formatNumber } from "@/lib/utils/formatters";

interface ProjectLimitSummaryFooterProps {
  query: UseQueryResult<SuccessFetchListResponse<ProjectLimitSummary>>;
  isFetching: boolean;
  selectedMonthRange: MonthRange;
}

const ProjectLimitSummaryFooter = ({
  query,
  selectedMonthRange,
  isFetching,
}: ProjectLimitSummaryFooterProps) => {
  const { t } = useTranslation();

  const { table } = useDataTableSettings();
  const { columnVisibility } = useTableColumnVisibility();

  const headers = table?.getHeaderGroups()?.[0]?.headers || [];

  // Calculate the width of the non-hour columns
  // by summing up the width of the non-hour columns
  // stored in the css variables
  const nonHourColumnStyle = useMemo(() => {
    const columns = headers
      .filter((header) => {
        return header.column.getIsVisible();
      })
      .filter((header) => {
        const column = table?.getColumn(header.id);
        const columnDef =
          column?.columnDef as ProjectLimitSummaryHoursColumnDef;

        if (!columnDef) return false;
        if (columnDef.meta?.isHoursColumn) return false;
        return true;
      })
      .map((header) => {
        return `var(--col-${header.id}-size) * 1px`;
      })
      .join(" + ");

    return {
      width: `calc(${columns})`,
    };
    // We need to re-run this when the column visibility changes
    // even if we don't use the columnVisibility variable explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, table, columnVisibility]);

  const hourColumnsStyles = useMemo(() => {
    return headers
      .filter((header) => {
        const column = table?.getColumn(header.id);
        const columnDef =
          column?.columnDef as ProjectLimitSummaryHoursColumnDef;
        return columnDef?.meta?.isHoursColumn;
      })
      .reduce(
        (acc, header) => {
          acc[header.id] = {
            width: `calc(var(--col-${header.id}-size) * 1px)`,
          };
          return acc;
        },
        {} as Record<string, { width: string }>,
      );
    // We need to re-run this when the column visibility changes
    // even if we don't use the columnVisibility variable explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, table, columnVisibility]);

  const hourColumnIds = useMemo(
    () =>
      headers
        .filter((header) => {
          const column = table?.getColumn(header.id);
          const columnDef =
            column?.columnDef as ProjectLimitSummaryHoursColumnDef;
          return columnDef?.meta?.isHoursColumn;
        })
        .map((header) => header.id),
    // We need to re-run this when the column visibility changes
    // even if we don't use the columnVisibility variable explicitly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headers, table, columnVisibility],
  );

  const hoursPerPeriod = useMemo(
    () =>
      query.data?.items.reduce(
        (acc, item) => {
          acc[item.period] = formatNumber(item.hours);
          return acc;
        },
        {} as Record<string, string>,
      ),
    [query.data],
  );

  const profitPerPeriod = useMemo(
    () =>
      query.data?.items.reduce(
        (acc, item) => {
          acc[item.period] = formatNumber(item.profit);
          return acc;
        },
        {} as Record<string, string>,
      ),
    [query.data],
  );

  const avgRatePerPeriod = useMemo(
    () =>
      query.data?.items.reduce(
        (acc, item) => {
          acc[item.period] = formatNumber(item.avg_rate);
          return acc;
        },
        {} as Record<string, string>,
      ),
    [query.data],
  );

  const periods = useMemo(
    () =>
      hourColumnIds.map((period) => ({
        period,
        hours: hoursPerPeriod?.[period],
        profit: profitPerPeriod?.[period],
        avgRate: avgRatePerPeriod?.[period],
      })),
    [hourColumnIds, hoursPerPeriod, profitPerPeriod, avgRatePerPeriod],
  );

  const cellClassName = "h-8 py-0 px-2";

  return (
    <TableFooter
      className={cn({
        "opacity-50 pointer-events-none blur-[2px]": isFetching,
      })}
    >
      <TableRow>
        <TableCell style={nonHourColumnStyle} className={cellClassName}>
          {t("projectLimits.summary.sumHours")}
        </TableCell>
        {periods.map(({ period, hours }) => (
          <TableCell
            key={period}
            style={hourColumnsStyles?.[period]}
            className={cellClassName}
          >
            {hours}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell style={nonHourColumnStyle} className={cellClassName}>
          {t("projectLimits.summary.sumProfit")}
        </TableCell>
        {periods.map(({ period, profit }) => (
          <TableCell
            key={period}
            style={hourColumnsStyles?.[period]}
            className={cellClassName}
          >
            {profit}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell style={nonHourColumnStyle} className={cellClassName}>
          {t("projectLimits.summary.avgRate")}
        </TableCell>
        {periods.map(({ period, avgRate }) => (
          <TableCell
            key={period}
            style={hourColumnsStyles?.[period]}
            className={cellClassName}
          >
            {avgRate}
          </TableCell>
        ))}
      </TableRow>
    </TableFooter>
  );
};

export default ProjectLimitSummaryFooter;
